export type MatchScoreDetail = {
  home: number | null;
  away: number | null;
};

export type Match = {
  id: string;
  league: string;
  time: string;
  date: string;
  home: string;
  away: string;
  homeFlag: string;
  awayFlag: string;
  odds: {
    home: number;
    draw: number;
    away: number;
    over25?: number;
    under25?: number;
    bttsYes?: number;
    bttsNo?: number;
  };
  venue?: string;
  stage?: string;
  homeForm?: ("W" | "D" | "L")[];
  awayForm?: ("W" | "D" | "L")[];
  h2h?: { date: string; competition: string; home: string; away: string; score: string }[];
  stats?: { label: string; home: string; away: string }[];
  markets?: { label: string; options: { label: string; odd: number }[] }[];
  // Fields from Firestore (API-Football) — basic
  status?: string;
  statusLabel?: string;
  isLive?: boolean;
  isFinished?: boolean;
  isScheduled?: boolean;
  isCancelled?: boolean;
  elapsed?: number | null;
  extraTime?: number | null;
  score?: MatchScoreDetail;
  scoreHalftime?: MatchScoreDetail;
  scoreFulltime?: MatchScoreDetail;
  scoreExtratime?: MatchScoreDetail;
  scorePenalty?: MatchScoreDetail;
  homeTeamLogo?: string | null;
  awayTeamLogo?: string | null;
  competitionLogo?: string | null;
  // Extended fields from normalized Firestore document
  fixtureId?: number;
  referenceId?: string;
  startTimestamp?: number | null;
  startDateStr?: string;
  startHour?: string;
  referee?: string | null;
  periods?: { first: number | null; second: number | null };
  region?: string;
  category?: string;
  competitionId?: number | null;
  competitionType?: string | null;
  competitionCountry?: string | null;
  competitionFlag?: string | null;
  competitionSeason?: number | null;
  homeTeamId?: number | null;
  awayTeamId?: number | null;
  teamIds?: number[];
  homeWinner?: boolean | null;
  awayWinner?: boolean | null;
  venueName?: string | null;
  venueCity?: string | null;
  venueId?: number | null;
  source?: string;
  updatedAt?: string | null;
};

// Match details (events, lineups, statistics) — fetched on demand
export type MatchDetails = {
  fixtureId: number;
  events: MatchEvent[];
  lineups: MatchLineup[];
  statistics: MatchStatistic[];
  isLive?: boolean;
  isFinished?: boolean;
};

export type MatchEvent = {
  time: { elapsed: number; extra: number | null };
  type: string;
  team: { id: number; name: string; logo: string };
  player: { id: number; name: string };
  assist?: { id: number; name: string } | null;
  detail: string;
};

export type MatchLineup = {
  team: { id: number; name: string; logo: string; colors: any };
  formation: string;
  startXI: { player: { id: number; name: string; number: number; pos: string; grid: string } }[];
  substitutes: { player: { id: number; name: string; number: number } }[];
  coach: { id: number; name: string; photo: string };
};

export type MatchStatistic = {
  team: { id: number; name: string; logo: string };
  statistics: { type: string; value: string }[];
};

const F = (s: string) => s.split("").filter((c) => "WDL".includes(c)) as ("W" | "D" | "L")[];
const defaultMarkets = (o: { home: number; draw: number; away: number }) => [
  {
    label: "Résultat final (1N2)",
    options: [
      { label: "1", odd: o.home },
      { label: "N", odd: o.draw },
      { label: "2", odd: o.away },
    ],
  },
  {
    label: "Double chance",
    options: [
      { label: "1X", odd: +(1 / (1 / o.home + 1 / o.draw)).toFixed(2) },
      { label: "12", odd: +(1 / (1 / o.home + 1 / o.away)).toFixed(2) },
      { label: "X2", odd: +(1 / (1 / o.draw + 1 / o.away)).toFixed(2) },
    ],
  },
  {
    label: "Plus / Moins 2.5 buts",
    options: [
      { label: "+2.5", odd: 1.85 },
      { label: "-2.5", odd: 1.95 },
    ],
  },
  {
    label: "Les deux équipes marquent",
    options: [
      { label: "Oui", odd: 1.72 },
      { label: "Non", odd: 2.05 },
    ],
  },
];

export const MATCHES: Match[] = [
  {
    id: "m1",
    league: "Coupe du Monde",
    time: "21:00",
    date: "Aujourd'hui",
    home: "France",
    away: "Angleterre",
    homeFlag: "🇫🇷",
    awayFlag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
    odds: { home: 1.95, draw: 3.4, away: 3.8 },
    venue: "Stade de France, Saint-Denis",
    stage: "Demi-finale",
    homeForm: F("WWDWL"),
    awayForm: F("WDWWW"),
    h2h: [
      { date: "Juin 2024", competition: "Amical", home: "France", away: "Angleterre", score: "2-1" },
      { date: "Déc. 2022", competition: "Coupe du Monde", home: "Angleterre", away: "France", score: "1-2" },
      { date: "Juin 2017", competition: "Amical", home: "France", away: "Angleterre", score: "3-2" },
      { date: "Nov. 2015", competition: "Amical", home: "Angleterre", away: "France", score: "2-0" },
    ],
    stats: [
      { label: "Buts / match", home: "2.1", away: "1.8" },
      { label: "Clean sheets", home: "6", away: "4" },
      { label: "Classement FIFA", home: "#2", away: "#5" },
    ],
  },
  {
    id: "m2",
    league: "Premier League",
    time: "18:30",
    date: "Aujourd'hui",
    home: "Man City",
    away: "Arsenal",
    homeFlag: "🔵",
    awayFlag: "🔴",
    odds: { home: 2.1, draw: 3.6, away: 3.2 },
    venue: "Etihad Stadium, Manchester",
    stage: "Journée 24",
    homeForm: F("WWWDW"),
    awayForm: F("WLWWD"),
    h2h: [
      { date: "Mars 2025", competition: "Premier League", home: "Arsenal", away: "Man City", score: "1-1" },
      { date: "Sept. 2024", competition: "Premier League", home: "Man City", away: "Arsenal", score: "2-2" },
      { date: "Fév. 2024", competition: "FA Cup", home: "Arsenal", away: "Man City", score: "0-1" },
      { date: "Oct. 2023", competition: "Premier League", home: "Arsenal", away: "Man City", score: "1-0" },
    ],
    stats: [
      { label: "Buts / match", home: "2.6", away: "2.2" },
      { label: "Possession", home: "63%", away: "58%" },
      { label: "Classement", home: "1er", away: "3e" },
    ],
  },
];

MATCHES.forEach((m) => {
  if (!m.markets) m.markets = defaultMarkets(m.odds);
});

// Reste des matchs (avec détails auto-générés)
const REST: Match[] = [
  {
    id: "m3",
    league: "La Liga",
    time: "22:00",
    date: "Demain",
    home: "Real Madrid",
    away: "Barcelone",
    homeFlag: "⚪",
    awayFlag: "🔵",
    odds: { home: 2.3, draw: 3.5, away: 2.8 },
    venue: "Santiago Bernabéu, Madrid",
    stage: "El Clásico · Journée 25",
    homeForm: F("WWDLW"),
    awayForm: F("WWWDW"),
    h2h: [
      { date: "Oct. 2024", competition: "La Liga", home: "Barcelone", away: "Real Madrid", score: "4-0" },
      { date: "Avr. 2024", competition: "La Liga", home: "Real Madrid", away: "Barcelone", score: "3-2" },
      { date: "Jan. 2024", competition: "Supercoupe", home: "Real Madrid", away: "Barcelone", score: "4-1" },
      { date: "Oct. 2023", competition: "La Liga", home: "Barcelone", away: "Real Madrid", score: "1-2" },
    ],
    stats: [
      { label: "Buts / match", home: "2.4", away: "2.7" },
      { label: "Titres en Liga", home: "36", away: "27" },
      { label: "Classement", home: "1er", away: "2e" },
    ],
  },
  {
    id: "m4",
    league: "Serie A",
    time: "20:45",
    date: "Demain",
    home: "Inter",
    away: "Juventus",
    homeFlag: "⚫",
    awayFlag: "⚪",
    odds: { home: 2.0, draw: 3.3, away: 3.5 },
    venue: "San Siro, Milan",
    stage: "Derby d'Italie · Journée 23",
    homeForm: F("WWDWW"),
    awayForm: F("DWWLW"),
    h2h: [
      { date: "Fév. 2025", competition: "Serie A", home: "Juventus", away: "Inter", score: "1-0" },
      { date: "Oct. 2024", competition: "Serie A", home: "Inter", away: "Juventus", score: "4-4" },
      { date: "Fév. 2024", competition: "Serie A", home: "Inter", away: "Juventus", score: "1-0" },
      { date: "Nov. 2023", competition: "Serie A", home: "Juventus", away: "Inter", score: "1-1" },
    ],
    stats: [
      { label: "Buts / match", home: "2.3", away: "1.9" },
      { label: "Clean sheets", home: "12", away: "10" },
      { label: "Classement", home: "1er", away: "4e" },
    ],
  },
  {
    id: "m5",
    league: "Ligue 1",
    time: "21:00",
    date: "Sam. 20 Juil.",
    home: "PSG",
    away: "Marseille",
    homeFlag: "🔵",
    awayFlag: "⚪",
    odds: { home: 1.65, draw: 3.9, away: 4.8 },
    venue: "Parc des Princes, Paris",
    stage: "Le Classique · Journée 22",
    homeForm: F("WWWWD"),
    awayForm: F("WDWLW"),
    h2h: [
      { date: "Mars 2025", competition: "Ligue 1", home: "Marseille", away: "PSG", score: "0-3" },
      { date: "Oct. 2024", competition: "Ligue 1", home: "PSG", away: "Marseille", score: "2-0" },
      { date: "Fév. 2024", competition: "Ligue 1", home: "Marseille", away: "PSG", score: "2-2" },
      { date: "Sept. 2023", competition: "Ligue 1", home: "PSG", away: "Marseille", score: "4-0" },
    ],
    stats: [
      { label: "Buts / match", home: "2.8", away: "1.7" },
      { label: "Domicile", home: "10V-0D", away: "5V-4D" },
      { label: "Classement", home: "1er", away: "3e" },
    ],
  },
  {
    id: "m6",
    league: "Bundesliga",
    time: "18:30",
    date: "Sam. 20 Juil.",
    home: "Bayern",
    away: "Dortmund",
    homeFlag: "🔴",
    awayFlag: "🟡",
    odds: { home: 1.75, draw: 3.8, away: 4.2 },
    venue: "Allianz Arena, Munich",
    stage: "Der Klassiker · Journée 21",
    homeForm: F("WWWDW"),
    awayForm: F("DLWWD"),
    h2h: [
      { date: "Fév. 2025", competition: "Bundesliga", home: "Dortmund", away: "Bayern", score: "1-1" },
      { date: "Nov. 2024", competition: "Bundesliga", home: "Bayern", away: "Dortmund", score: "3-0" },
      { date: "Mars 2024", competition: "Bundesliga", home: "Dortmund", away: "Bayern", score: "0-2" },
      { date: "Nov. 2023", competition: "Bundesliga", home: "Bayern", away: "Dortmund", score: "4-0" },
    ],
    stats: [
      { label: "Buts / match", home: "3.0", away: "2.1" },
      { label: "Titres consécutifs", home: "11", away: "0" },
      { label: "Classement", home: "1er", away: "3e" },
    ],
  },
];

REST.forEach((m) => {
  if (!m.markets) m.markets = defaultMarkets(m.odds);
  MATCHES.push(m);
});

export const getMatch = (id: string) => MATCHES.find((m) => m.id === id);

export type Transaction = {
  id: string;
  ref: string;
  type: "deposit" | "withdraw";
  platform: "1XBET" | "MELBET" | "BETWINNER";
  amount: number;
  status: "success" | "pending" | "failed";
  createdAt: string;
  accountId: string;
};

export const TRANSACTIONS: Transaction[] = [
  {
    id: "t1",
    ref: "ES-9283A2",
    type: "deposit",
    platform: "1XBET",
    amount: 5000,
    status: "success",
    createdAt: "Aujourd'hui · 14:32",
    accountId: "168773863",
  },
  {
    id: "t2",
    ref: "ES-9271B1",
    type: "withdraw",
    platform: "MELBET",
    amount: 12500,
    status: "success",
    createdAt: "Aujourd'hui · 11:04",
    accountId: "928374102",
  },
  {
    id: "t3",
    ref: "ES-9265F4",
    type: "deposit",
    platform: "BETWINNER",
    amount: 2500,
    status: "pending",
    createdAt: "Hier · 22:11",
    accountId: "471028394",
  },
  {
    id: "t4",
    ref: "ES-9250K7",
    type: "deposit",
    platform: "1XBET",
    amount: 10000,
    status: "success",
    createdAt: "Hier · 18:45",
    accountId: "168773863",
  },
  {
    id: "t5",
    ref: "ES-9241X9",
    type: "withdraw",
    platform: "1XBET",
    amount: 7500,
    status: "failed",
    createdAt: "Il y a 2 jours",
    accountId: "168773863",
  },
];

export const PLATFORM_ACCENT: Record<"1XBET" | "MELBET" | "BETWINNER", string> = {
  "1XBET": "#2563eb",
  MELBET: "#f5b301",
  BETWINNER: "#10b981",
};